/** Crop definitions — pure data. Adding a crop here (and seeds to the shop/inventory)
 *  requires ZERO engine-code changes: textures, items, growth all derive from this. */
import type { SeasonId } from '../sim/seasons';

export interface CropDef {
  id: string;
  name: string;
  /** Item id of the seed that plants this crop. */
  seedItemId: string;
  /** Item id of the harvested produce. */
  produceItemId: string;
  /** In-game days of *watered* growth from planting to first harvest. */
  growthDays: number;
  /** Number of growth-stage frames (seed → mature), inclusive. Min 2. */
  stages: number;
  /** After harvest, days to regrow for another harvest. null = single-use. */
  regrowDays: number | null;
  /** Units of produce per harvest. */
  yield: number;
  /** Produce sell value (shipping bin). */
  sellPrice: number;
  /** Seed buy price (shop). */
  seedPrice: number;
  /** Short flavour shown in popups/shop. */
  description: string;
  /** Placeholder art tint — final art overrides by texture key. */
  color: number;
}

export const CROPS: Record<string, CropDef> = {
  turnip: { id: 'turnip', name: 'Turnip', seedItemId: 'seed_turnip', produceItemId: 'turnip',
    growthDays: 3, stages: 4, regrowDays: null, yield: 1, sellPrice: 35, seedPrice: 20,
    description: 'A quick, cheerful root. Ready in 3 days.', color: 0xc9a0dc },
  potato: { id: 'potato', name: 'Potato', seedItemId: 'seed_potato', produceItemId: 'potato',
    growthDays: 5, stages: 4, regrowDays: null, yield: 2, sellPrice: 40, seedPrice: 25,
    description: 'Hearty and reliable — yields two. 5 days.', color: 0xd9a066 },
  carrot: { id: 'carrot', name: 'Carrot', seedItemId: 'seed_carrot', produceItemId: 'carrot',
    growthDays: 4, stages: 4, regrowDays: null, yield: 1, sellPrice: 35, seedPrice: 20,
    description: 'Crunchy and sweet. 4 days.', color: 0xe08e45 },
  tomato: { id: 'tomato', name: 'Tomato', seedItemId: 'seed_tomato', produceItemId: 'tomato',
    growthDays: 6, stages: 5, regrowDays: 2, yield: 1, sellPrice: 60, seedPrice: 50,
    description: 'Keeps giving — regrows every 2 days.', color: 0xe05a4a },
  strawberry: { id: 'strawberry', name: 'Strawberry', seedItemId: 'seed_strawberry', produceItemId: 'strawberry',
    growthDays: 5, stages: 5, regrowDays: 3, yield: 1, sellPrice: 50, seedPrice: 60,
    description: 'Sweet berries that regrow every 3 days.', color: 0xe23a5e },
  pumpkin: { id: 'pumpkin', name: 'Pumpkin', seedItemId: 'seed_pumpkin', produceItemId: 'pumpkin',
    growthDays: 7, stages: 5, regrowDays: null, yield: 1, sellPrice: 160, seedPrice: 50,
    description: 'Big, slow, and very valuable. 7 days.', color: 0xe08a3c },
  corn: { id: 'corn', name: 'Corn', seedItemId: 'seed_corn', produceItemId: 'corn',
    growthDays: 6, stages: 4, regrowDays: null, yield: 2, sellPrice: 45, seedPrice: 35,
    description: 'Sweet cobs — yields two. 6 days.', color: 0xf2d24a },
  pepper: { id: 'pepper', name: 'Pepper', seedItemId: 'seed_pepper', produceItemId: 'pepper',
    growthDays: 5, stages: 4, regrowDays: 3, yield: 1, sellPrice: 45, seedPrice: 40,
    description: 'Spicy, and regrows every 3 days.', color: 0xe0662a },
  eggplant: { id: 'eggplant', name: 'Eggplant', seedItemId: 'seed_eggplant', produceItemId: 'eggplant',
    growthDays: 6, stages: 5, regrowDays: 3, yield: 1, sellPrice: 55, seedPrice: 50,
    description: 'Glossy purple. Regrows. 6 days.', color: 0x7a4a9a },
  melon: { id: 'melon', name: 'Melon', seedItemId: 'seed_melon', produceItemId: 'melon',
    growthDays: 8, stages: 5, regrowDays: null, yield: 1, sellPrice: 200, seedPrice: 60,
    description: 'Big, juicy, and very valuable. 8 days.', color: 0x6ab04a },
  cabbage: { id: 'cabbage', name: 'Cabbage', seedItemId: 'seed_cabbage', produceItemId: 'cabbage',
    growthDays: 5, stages: 4, regrowDays: null, yield: 1, sellPrice: 55, seedPrice: 35,
    description: 'A big leafy head. 5 days.', color: 0x6ab04a },
  squash: { id: 'squash', name: 'Squash', seedItemId: 'seed_squash', produceItemId: 'squash',
    growthDays: 6, stages: 4, regrowDays: null, yield: 1, sellPrice: 70, seedPrice: 45,
    description: 'Hearty autumn squash. 6 days.', color: 0xe08e2a },
  leek: { id: 'leek', name: 'Leek', seedItemId: 'seed_leek', produceItemId: 'leek',
    growthDays: 5, stages: 4, regrowDays: null, yield: 1, sellPrice: 60, seedPrice: 40,
    description: 'Mild and oniony. 5 days.', color: 0x9ec96a },
};

export const cropList = (): CropDef[] => Object.values(CROPS);
export const getCrop = (id: string): CropDef | undefined => CROPS[id];

/** Seasons each crop thrives in. Absent/empty = hardy (any season). Off-season = slower growth + smaller harvest. */
const CROP_SEASONS: Record<string, SeasonId[]> = {
  turnip: ['spring', 'fall'],
  potato: ['spring', 'fall'],
  carrot: ['spring', 'fall'],
  strawberry: ['spring'],
  tomato: ['summer'],
  pepper: ['summer'],
  melon: ['summer'],
  corn: ['summer', 'fall'],
  pumpkin: ['fall'],
  eggplant: ['fall'],
  cabbage: ['spring'],
  squash: ['fall'],
  leek: ['fall'],
};
/** True if the crop thrives in the given season (hardy crops thrive everywhere). */
export function cropInSeason(cropId: string, season: SeasonId): boolean {
  const s = CROP_SEASONS[cropId];
  return !s || s.length === 0 || s.includes(season);
}
